import { Fragment, useRef, useState } from 'react';
import { clamp, merge } from 'lodash-es';

import { linkClasses as muiLinkClasses, styled } from '@mui/material';
import { IcMoreHorizontalRegular } from '@kep/kcds-icons';

import { buttonClasses } from '../Button';
import { DropdownMenu2 } from '../DropdownMenu2';
import { IconButtonGhost } from '../IconButtonGhost';
import { Link } from '../Link';
import { MenuOptionItem } from '../MenuOptionItem';
import _BreadcrumbsItem from './BreadcrumbsItem';
import { breadcrumbsClasses } from './theme';

import type { LinkProps } from '../Link';
import type { BreadcrumbItemData, BreadcrumbsOwnerState, BreadcrumbsProps } from './types';

export default function Breadcrumbs(props: BreadcrumbsProps) {
  const {
    itemIndexToCollapse = 1,
    items,
    maxItems: maxItemsProp = 5,
    separator = '/',
    slotProps,
    slots,
    ...others
  } = props;
  const { link: LinkSlotComponent = Link } = slots ?? {};
  const { link: linkSlotProps } = merge(
    {
      link: { textVariant: 'inline', underline: 'hover' } as LinkProps,
    },
    slotProps,
  );

  const ownerState: BreadcrumbsOwnerState = {
    ...props,
    itemIndexToCollapse,
    maxItems: maxItemsProp,
    separator,
  };

  const maxItems = clamp(maxItemsProp, 1, items.length);
  const needToCollapse = items.length > maxItems;
  const itemIndexToCollapseStart = clamp(itemIndexToCollapse, 0, maxItems - 1);
  const itemIndexToCollapseEnd = clamp(
    itemIndexToCollapseStart + items.length - maxItems,
    0,
    items.length - 1,
  );

  const [open, setOpen] = useState(false);
  const collapsedMenuRef = useRef<HTMLButtonElement>(null);

  const renderItem = (item: BreadcrumbItemData) => {
    return item.href ? (
      <BreadcrumbsItem ownerState={ownerState}>
        <LinkSlotComponent aria-label={item['aria-label']} href={item.href} {...linkSlotProps}>
          {item.label}
        </LinkSlotComponent>
      </BreadcrumbsItem>
    ) : (
      <BreadcrumbsItem aria-label={item['aria-label']} ownerState={ownerState}>
        {item.label}
      </BreadcrumbsItem>
    );
  };

  const displayedItem = needToCollapse
    ? [
        ...items.slice(0, itemIndexToCollapseStart).map(renderItem),
        renderItem({
          label: (
            <BreadcrumbsExpandButton
              ref={collapsedMenuRef}
              size="sm"
              onClick={() => setOpen(prev => !prev)}
              ownerState={ownerState}
            >
              <IcMoreHorizontalRegular />
            </BreadcrumbsExpandButton>
          ),
        }),
        ...items.slice(itemIndexToCollapseEnd + 1, items.length).map(renderItem),
      ]
    : items.map(renderItem);

  const collapsedItems = items.slice(itemIndexToCollapseStart, itemIndexToCollapseEnd + 1);

  return (
    <BreadcrumbsRoot ownerState={ownerState} {...others}>
      {displayedItem.map((content, index) => (
        <Fragment key={index}>
          {index > 0 && (
            <BreadcrumbsSeparator ownerState={ownerState}>{separator}</BreadcrumbsSeparator>
          )}
          {content}
        </Fragment>
      ))}

      <DropdownMenu2
        anchorEl={collapsedMenuRef.current}
        open={open}
        onClose={() => setOpen(false)}
        items={collapsedItems.map(item => ({
          text: item.label,
          renderItem: params => (
            <MenuOptionItem
              {...params}
              key={params.key}
              slots={{ root: LinkSlotComponent }}
              slotProps={{ root: { href: item.href, ...linkSlotProps } }}
              style={{ width: 'initial' }}
            />
          ),
        }))}
      />
    </BreadcrumbsRoot>
  );
}

const BreadcrumbsRoot = styled('nav', {
  target: breadcrumbsClasses.root,
})<{ ownerState: BreadcrumbsProps }>`
  display: flex;
  align-items: center;
  padding: 1.5px 0px;
`;

const BreadcrumbsItem = styled(_BreadcrumbsItem, {
  target: breadcrumbsClasses.item,
})<{ ownerState: BreadcrumbsProps }>`
  ${({ theme }) => theme.typography['label/regular/xs']};
  margin: -4px;
  padding: 4px;
  color: ${({ theme }) => theme.color['text/weak/default']};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  & .${muiLinkClasses.root} {
    display: inline;
  }

  &:has(.${breadcrumbsClasses.expandButton}) {
    min-width: calc(24px + 8px);
  }
`;

const BreadcrumbsSeparator = styled('span', {
  target: breadcrumbsClasses.separator,
})<{ ownerState: BreadcrumbsProps }>`
  ${({ theme }) => theme.typography['label/regular/md']};
  margin: 0px 5.5px;
  color: ${({ theme }) => theme.color['text/disabled']};
`;

const BreadcrumbsExpandButton = styled(IconButtonGhost, {
  target: breadcrumbsClasses.expandButton,
})<{ ownerState: BreadcrumbsProps }>`
  color: ${({ theme }) => theme.color['icon/weakest/default']};
  padding: 0;
  width: 24px;
  min-width: 24px;
  height: 16px;
  min-height: 16px;

  .${buttonClasses.label} {
    display: flex;
    width: 16px;
    height: 16px;
  }
`;
